import { sdk } from '@needful-things-mvp/sdk';
import { Boxes, FolderKanban, Mail, Palette, Vote } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { IS_DEVELOPMENT } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const highlights = [
  {
    icon: FolderKanban,
    title: 'Projects',
    description: 'Create projects and move them through each lifecycle stage.',
  },
  {
    icon: Palette,
    title: 'Artists & proposals',
    description: 'Manage artists, competing proposals, and media.',
  },
  {
    icon: Vote,
    title: 'Voting',
    description: 'Open voting periods and review the results.',
  },
  {
    icon: Mail,
    title: 'Voter emails',
    description: 'Capture optional emails and export the data.',
  },
];

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2">
      <div className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Boxes className="size-5" />
      </div>
      <span className="font-semibold text-lg tracking-tight">NeedfulThings</span>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/';

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleGoogleLogin() {
    setError(null);

    // Development: skip OAuth and sign in locally.
    if (IS_DEVELOPMENT) {
      setAuthenticated();
      navigate(redirectTo, { replace: true });
      return;
    }

    setLoading(true);
    try {
      await sdk.auth.oauth2.initiate('google');
      setAuthenticated();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-svh md:grid-cols-2">
      <main className="flex flex-col justify-center bg-background p-6 sm:p-10 lg:p-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8">
            <BrandMark />
          </div>

          <div className="mb-6 space-y-1.5">
            <h1 className="font-semibold text-2xl tracking-tight">Welcome back</h1>
            <p className="text-muted-foreground text-sm">Sign in to manage your projects.</p>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            style={{ fontFamily: 'Roboto, system-ui, sans-serif' }}
            className="relative inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-md border border-[#dadce0] bg-white px-3 font-medium text-[#1f1f1f] text-sm transition-colors hover:bg-[#f8f9fa] active:bg-[#f1f3f4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a73e8] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#dadce0] dark:bg-white dark:text-[#1f1f1f] dark:hover:bg-[#f8f9fa]"
          >
            <span className="absolute left-4 flex items-center">
              <GoogleIcon className="size-[18px]" />
            </span>
            {loading ? 'Signing in…' : 'Continue with Google'}
          </button>

          {error && <p className="mt-3 text-destructive text-sm">{error}</p>}

          <p className="mt-6 text-muted-foreground text-xs leading-relaxed">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-foreground underline underline-offset-4">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-foreground underline underline-offset-4">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </main>

      <aside className="relative hidden flex-col justify-center overflow-hidden p-10 text-white [background-image:linear-gradient(135deg,#18181b_0%,#27272a_100%)] md:flex lg:p-16">
        <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.18)_1px,transparent_0)] [background-size:22px_22px]" />

        <div className="relative max-w-md space-y-8">
          <div className="space-y-3">
            <h2 className="font-semibold text-2xl leading-tight tracking-tight">Needful Admin</h2>
            <p className="text-sm leading-6 text-white/70">
              Manage projects, artists, proposals, media, and voting in one place.
            </p>
          </div>

          <ul className="space-y-4">
            {highlights.map((item) => (
              <li key={item.title} className="flex items-start gap-3">
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <item.icon className="size-4" />
                </div>
                <div>
                  <p className="font-medium text-sm">{item.title}</p>
                  <p className="text-white/60 text-xs">{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="absolute bottom-10 left-10 text-xs text-white/50 lg:bottom-16 lg:left-16">
          © {new Date().getFullYear()} NeedfulThings
        </p>
      </aside>
    </div>
  );
}
